import React, { JSX } from 'react';
import { Box, SxProps, Tab, Tabs } from '@mui/material';
import { Tune as TuneIcon } from '@mui/icons-material';
import DescriptionIcon from '@mui/icons-material/Description';
import { BackstoryTextFieldRef } from 'components/BackstoryTextField';
import { ConversationHandle } from 'components/Conversation';
import {
  Candidate,
  CandidateQuestion,
  ChatMessage,
  ChatMessageError,
  ChatMessageStatus,
  ChatSession,
  Resume,
} from 'types/types';
import { ChatPanelContent } from './CandidateChatPanel';
import { ResumePanelContent } from './CandidateChatResumePanel';

interface CandidateChatLayoutProps {
  sx?: SxProps;
  forwardedRef: React.ForwardedRef<ConversationHandle>;
  isMobile: boolean;
  tabValue: string;
  onTabChange: (value: string) => void;
  resume: Resume | null;
  selectedCandidate: Candidate;
  chatSession: ChatSession | null;
  messages: ChatMessage[];
  processingMessage: ChatMessage | ChatMessageError | ChatMessageStatus | null;
  streamingMessage: ChatMessage | null;
  streaming: boolean;
  loading: boolean;
  defaultQuestions: string[];
  welcomeMessage: ChatMessage;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  backstoryTextRef: React.RefObject<BackstoryTextFieldRef | null>;
  onDelete: (session: ChatSession) => Promise<void>;
  onSendMessage: (message: string) => Promise<void>;
  onSubmitQuestion: (question: CandidateQuestion) => void;
}

function CandidateChatLayout(props: CandidateChatLayoutProps): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%',
        minHeight: 0,
        overflow: props.isMobile ? 'hidden' : undefined,
        p: 0,
        maxHeight: '100%',
        position: 'relative',
      }}
    >
      {props.isMobile && <MobileTabs tabValue={props.tabValue} onTabChange={props.onTabChange} />}
      <Box
        ref={props.forwardedRef}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: props.isMobile ? '1 1 0' : undefined,
          height: props.isMobile ? 'auto' : '100%',
          width: '100%',
          minHeight: 0,
          maxHeight: props.isMobile ? 'none' : '100%',
          '& > *:not(.Scrollable)': { flexShrink: 0 },
          ...props.sx,
          p: 0,
          m: 0,
          backgroundColor: '#D3CDBF',
        }}
      >
        {(!props.isMobile || props.tabValue === 'resume') && props.resume && (
          <ResumePanelContent resume={props.resume} isMobile={props.isMobile} />
        )}
        {(!props.isMobile || props.tabValue === 'chat') && <ChatPanelContent {...props} />}
      </Box>
    </Box>
  );
}

function MobileTabs({
  tabValue,
  onTabChange,
}: {
  tabValue: string;
  onTabChange: (value: string) => void;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: '0.8rem',
        minHeight: 36,
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        backgroundColor: 'background.paper',
      }}
    >
      <Tabs
        value={tabValue}
        onChange={(_, value): void => onTabChange(value)}
        centered
        sx={{ minHeight: 36, '& .MuiTabs-indicator': { height: 2 } }}
      >
        <Tab
          value="resume"
          sx={mobileTabSx}
          icon={<DescriptionIcon sx={{ height: '16px' }} />}
          label="Resume"
        />
        <Tab
          value="chat"
          sx={mobileTabSx}
          icon={<TuneIcon sx={{ height: '16px' }} />}
          label="Chat"
        />
      </Tabs>
    </Box>
  );
}

const mobileTabSx = { minHeight: 36, maxHeight: 36, px: 1.5, py: 0, m: 0, fontSize: '0.72rem' };

export { CandidateChatLayout };
